import { Button } from '@base-ui/react/button';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import buttonStyles from '~/styles/button.module.css';

// Readable line lengths on wide screens: the form beside a toolbox that keeps
// Save and status in view.
export const EditorLayout = styled.div`
  align-items: start;
  display: grid;
  gap: 1.25rem;
  grid-template-columns: minmax(0, 1fr) 18rem;
  max-width: 76rem;

  @media (max-width: 62rem) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const EditorMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

export const EditorPanel = styled.aside`
  background-color: var(--component-color);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 1rem;

  @media (max-width: 62rem) {
    position: static;

    /* Save moves below the settings, so the group above it opens the panel. */
    & > *:nth-child(2) {
      border-top: none;
    }
  }
`;

export const PanelGroup = styled.div`
  border-top: 1px solid var(--grey-200);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;

  &:first-child {
    border-top: none;
  }
`;

// Save and Cancel, and the danger zone under them, in that order once the
// panel is stacked below the form.
const SaveShell = styled(PanelGroup)`
  @media (max-width: 62rem) {
    order: 1;

    &:first-child {
      border-top: 1px solid var(--grey-200);
    }
  }
`;

export const DangerGroup = styled(PanelGroup)`
  @media (max-width: 62rem) {
    order: 2;
  }
`;

export const PanelActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  & > * {
    flex: 1;
  }
`;

export type SectionTone = 'content' | 'answers' | 'feedback' | 'settings';

const TONE_COLORS: Record<SectionTone, string> = {
  content: 'var(--tone-content)',
  answers: 'var(--tone-answers)',
  feedback: 'var(--tone-feedback)',
  settings: 'var(--tone-settings)',
};

// A colored card per kind of input, so the eye lands on the right group.
export function FormSection({
  tone,
  icon,
  title,
  description,
  children,
}: {
  tone: SectionTone;
  icon: IconDefinition;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card $color={TONE_COLORS[tone]}>
      <CardHeader>
        <IconChip $color={TONE_COLORS[tone]}>
          <FontAwesomeIcon icon={icon} />
        </IconChip>
        <div>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );
}

const Card = styled.section<{ $color: string }>`
  background-color: var(--component-color);
  border-radius: 0.5rem;
  border-top: 3px solid ${(p) => p.$color};
  box-shadow: var(--shadow-md);
`;

const CardHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--grey-200);
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
`;

const IconChip = styled.span<{ $color: string }>`
  align-items: center;
  background: color-mix(in srgb, ${(p) => p.$color} 14%, transparent);
  border-radius: 0.5rem;
  color: ${(p) => p.$color};
  display: inline-flex;
  flex-shrink: 0;
  height: 2rem;
  justify-content: center;
  width: 2rem;
`;

const CardTitle = styled.h2`
  color: var(--grey-900);
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
`;

const CardDescription = styled.p`
  color: var(--grey-600);
  font-size: 0.8125rem;
  margin: 0.125rem 0 0;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1rem;
`;

export const PanelLabel = styled.span`
  color: var(--grey-600);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

// Read views share the editor's width limit.
export const DetailPage = styled.div`
  max-width: 76rem;
`;

// Top of the toolbox: Save and Cancel, with what's stopping a save beneath.
export function SaveGroup({
  heading,
  dirty,
  saving,
  saveDisabled = false,
  onSave,
  onCancel,
  children,
}: {
  heading: ReactNode;
  dirty: boolean;
  saving: boolean;
  saveDisabled?: boolean;
  onSave: () => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  return (
    <SaveShell>
      <PanelHeading>
        {heading}
        {dirty && <Unsaved>Unsaved changes</Unsaved>}
      </PanelHeading>
      <PanelActions>
        <Button
          className={`${buttonStyles.Button} ${buttonStyles.Primary}`}
          onClick={onSave}
          disabled={saving || saveDisabled}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        {/* A save in flight lands whatever Cancel says, so wait for it. */}
        <Button
          className={buttonStyles.Button}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
      </PanelActions>
      {children}
    </SaveShell>
  );
}

const PanelHeading = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-between;
  min-height: 1.75rem;
`;

const Unsaved = styled.span`
  color: var(--orange);
  font-size: 0.75rem;
  font-weight: 600;
`;
